import React, { useContext, useEffect, useState } from 'react';
import { Label, Card } from '@cargomatic/ui';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import CustomerAPIUtils, {
  getCustomerV2,
  sendShipperRateCardEmail,
} from 'utils/CustomersAPIUtils';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import { showToast } from 'stores/ToastStore';
import { LocaleContext } from 'providers/locale-settings-context';
import { FSC_TYPE } from 'constants/fsc/fsc';
import { getSignedUrl, getDocuments } from 'utils/DocumentsAPIUtils';
import RatesSettingsLayout from '../modules/user/rates/RatesSettingsLayout';
import fileDownload from 'react-file-download';
import SendRateCardEmailModal from 'components/modules/user/rates/SendRateCardEmailModal';
import useModal from 'hooks/useModal';
import { getRateCardEmailRecipient } from 'helpers/shipments';
import LoadingCircle from 'components/ui/LoadingCircle';
import { unescapeAmpersand } from 'helpers/helper_functions';
import SessionStore from 'stores/SessionStore';

const RateCard = () => {
  const { t } = useTranslation();
  const { id: accountId } = useParams();
  const { formatDate } = useContext(LocaleContext);

  const [account, setAccount] = useState({});
  const [loading, setLoading] = useState(false);
  const [rateCardGenerationInProgress, setRateCardGenerationInProgress] =
    useState(false);
  const [recentRateCard, setRecentRateCard] = useState({});
  const [translateOptions, setTranslateOptions] = useState({});
  const {
    visible: rateCardEmailVisible,
    show: showRateCardEmailModal,
    close: closeRateCardEmailModal,
  } = useModal();

  useEffect(() => {
    fetchAccount();
    generateLocalizationSlugs();
  }, []);

  const fetchDocuments = async (docType) => {
    const { data } = await getDocuments(accountId, docType, {
      order: {
        field: 'createdAt',
        direction: 'desc',
      },
    });

    const { getDocuments: docsData } = data;
    if (docsData?.documents?.length) {
      const { key: documentKey, createdAt: generatedOn } =
        docsData.documents[0];
      const pdfUrl = await getPdfUrl(documentKey);
      setRecentRateCard({ documentKey, generatedOn, pdfUrl });
    }
  };

  async function fetchAccount() {
    try {
      setLoading(true);
      const account = await getCustomerV2(accountId);
      const documentType = account?._type === 'Shipper' ? 'customer_rate_card' : 'carrier_rate_card';
      await fetchDocuments(documentType);
      setAccount(account);
    } catch (e) {
      showToast('', t('FAILED_FETCHING_SHIPPER_DETAILS'), 'error');
    } finally {
      setLoading(false);
    }
  }

  const generateLocalizationSlugs = () => {
    const currency = t('CURRENCY_SIGN');

    const initFscTypes = {
      VIA_INTEGRATION: t('FS_SERVICE'),
      FLAT_DOLLAR: t('FLAT'),
      FLAT_PERCENT: t('PERCENTAGE_LH'),
      VARIABLE_DOLLAR_PER_MILE: t('VARIABLE_AMOUNT_PER_MILE'),
    };

    const fscTypes = Object.keys(FSC_TYPE).reduce((acc, slug) => {
      if (!initFscTypes[slug]) {
        const translation = t(slug);
        if (slug === translation) {
          acc[slug] = FSC_TYPE[slug];
        } else {
          acc[slug] = translation;
        }
      }
      return acc;
    }, initFscTypes);

    setTranslateOptions({
      currency,
      fscTypes,
    });
  };

  const generateRateCard = async () => {
    try {
      setRateCardGenerationInProgress(true);
      const createdDate = formatDate(Date.now());
      const shipperName = unescapeAmpersand(account?.account_name);
      const billToName = unescapeAmpersand(
        account?.billing_address?.name || shipperName,
      );

      // Common fields
      const commonPayload = {
        customerId: account?._id,
        createdDate,
        billToName,
        localizationSlugs: translateOptions,
        languageCodeId: LOCALE_CODE,
      };

      // Shipper-specific payload
      const shipperPayload = {
        ...commonPayload,
        shipperName: unescapeAmpersand(account?.account_name),
        billToName: unescapeAmpersand(
          account?.billing_address?.name || account?.account_name,
        ),
        salesRep: account?.sales_rep,
      };

      // Carrier-specific payload
      const carrierPayload = {
        ...commonPayload,
        carrierName: unescapeAmpersand(account?.name),
        billToName: unescapeAmpersand(
          account?.billing_address?.name || account?.name,
        ),
      };

      const userType = account?._type;
      const payload = userType === 'Shipper' ? shipperPayload : carrierPayload;

      const result = await CustomerAPIUtils.generateRateCard(payload);
      const { documentKey, createdAt: generatedOn } = result;
      const pdfUrl = await getPdfUrl(documentKey);
      setRecentRateCard({ documentKey, generatedOn, pdfUrl });
    } catch (e) {
    } finally {
      setRateCardGenerationInProgress(false);
    }
  };

  const sendRateCardEmail = (recipients) => {
    sendShipperRateCardEmail(accountId, recipients, recentRateCard);
  };

  const getRateCardEmailRecipientList = (account) => {
    const loggedInUserEmail = SessionStore.getUser()?.email || '';
    const recipients = getRateCardEmailRecipient(account);
    return [recipients, loggedInUserEmail].filter(Boolean).join(', ').trim();
  }

  const getPdfUrl = async (docKey) => {
    try {
      const {
        data: { getSignedUrl: getSignedUrlData },
      } = await getSignedUrl(docKey);
      return getSignedUrlData?.signedUrl;
    } catch (e) {
      showToast('', t('GENERATE_AGAIN_RATE_CARD'), 'error');
    }
  };

  const handleDownload = async () => {
    const response = await fetch(recentRateCard?.pdfUrl);
    const blob = await response.blob();
    fileDownload(blob, 'rate-card.pdf');
  };

  const renderMenu = () => (
    <div className="align-self-end">
      <Dropdown useIcon>
        <Dropdown.Button
          onClick={generateRateCard}
          disabled={rateCardGenerationInProgress}
        >
          {recentRateCard?.pdfUrl ? t('REGENERATE') : t('GENERATE')}
        </Dropdown.Button>
        {recentRateCard?.pdfUrl && (
          <Dropdown.Button
            onClick={showRateCardEmailModal}
            disabled={rateCardGenerationInProgress}
          >
            {t('SEND_EMAIL')}
          </Dropdown.Button>
        )}
      </Dropdown>
    </div>
  );

  const renderDownloadLink = () => {
    if (rateCardGenerationInProgress) {
      return <LoadingCircle text={t('RATE_CARD_GENERATION_IN_PROGRESS')} />;
    }
    if (recentRateCard?.pdfUrl) {
      return <a onClick={handleDownload}>{t('DOWNLOAD_RATE_CARD')}</a>;
    }
    return <p>{t('RATE_CARD_NOT_AVAILABLE')}</p>;
  };

  const renderRecentRateCard = () => {
    return (
      <>
        {renderDownloadLink()}
        {!rateCardGenerationInProgress && (
          <DescriptionListItem label={t('GENERATED_ON')}>
            <Label>{formatDate(recentRateCard?.generatedOn) || '-'}</Label>
          </DescriptionListItem>
        )}
        {renderMenu()}
      </>
    );
  };

  return loading ? (
    <LoadingIndicator />
  ) : (
    <RatesSettingsLayout activeTab="drayageRateCard" account={account}>
      <Label className="text-lg">{t('GENERATE_RATE_CARD')}</Label>
      <Card className="w-full mt-2 p-4">
        <div className="w-full flex flex-col sm:flex-row justify-between">
          {renderRecentRateCard()}
        </div>
      </Card>
      {rateCardEmailVisible && (
        <SendRateCardEmailModal
          show={rateCardEmailVisible}
          onHide={closeRateCardEmailModal}
          onSend={sendRateCardEmail}
          defaultRecipient={getRateCardEmailRecipientList(account)}
        />
      )}
    </RatesSettingsLayout>
  );
};

export default RateCard;
